import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getOrders, 
  resolveDispute,
  getOrderStatistics 
} from '../../services/api';

function OrderDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolutionData, setResolutionData] = useState({
    resolution: '',
    refund_amount: 0,
    resolution_notes: ''
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderIdParam = searchParams.get('order');

  useEffect(() => {
    fetchDisputes();
    fetchDisputeStats();
  }, []);

  useEffect(() => {
    if (orderIdParam && disputes.length > 0) {
      const dispute = disputes.find(d => d.order_id == orderIdParam);
      if (dispute) {
        setSelectedDispute(dispute);
        setShowResolutionModal(true);
      }
    }
  }, [orderIdParam, disputes]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await getOrders({ disputed: 'true' });
      setDisputes(response.data.orders);
    } catch (err) {
      setError('Failed to load disputes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputeStats = async () => {
    try {
      const response = await getOrderStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load dispute stats:', err);
    }
  };

  const handleResolveDispute = async () => {
    if (!resolutionData.resolution) {
      setError('Please select a resolution type');
      return;
    }

    if (resolutionData.resolution === 'partial_refund' && !resolutionData.refund_amount) {
      setError('Please enter refund amount for partial refund');
      return;
    }

    try {
      setActionLoading(true);
      await resolveDispute(selectedDispute.order_id, resolutionData);
      setShowResolutionModal(false);
      setSelectedDispute(null);
      setResolutionData({
        resolution: '',
        refund_amount: 0,
        resolution_notes: ''
      });
      
      // Refresh the disputes list
      await fetchDisputes();
      await fetchDisputeStats();
    } catch (err) {
      setError(`Failed to resolve dispute: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openResolutionModal = (dispute) => {
    setSelectedDispute(dispute);
    setResolutionData({
      resolution: '',
      refund_amount: 0,
      resolution_notes: ''
    });
    setShowResolutionModal(true);
  };

  const getDisputeSeverity = (order) => {
    const created = new Date(order.created_at);
    const now = new Date();
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    
    if (hoursDiff > 72) return 'high'; // Over 3 days
    if (hoursDiff > 24) return 'medium'; // 1-3 days
    return 'low'; // Less than 1 day
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'high':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">🔥 High</span>;
      case 'medium':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">⚠️ Medium</span>;
      case 'low':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">💡 Low</span>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'accepted':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Accepted</span>;
      case 'shipped':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Shipped</span>;
      case 'completed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Completed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  if (loading && disputes.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading disputes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dispute Resolution Center</h1>
            <p className="text-gray-600 mt-1">Manage and resolve order disputes between buyers and sellers</p>
          </div>
          <button
            onClick={() => navigate('/admin/orders')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View All Orders
          </button>
        </div>
      </div>

      {/* Dispute Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Disputes</p>
                <p className="text-2xl font-bold text-red-600">{disputes.length}</p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <span className="text-red-600">⚖️</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              All time: {stats.dispute_stats?.[0]?.total_disputes || 0}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Disputes</p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats.dispute_stats?.[0]?.active_disputes || 0}
                </p>
              </div>
              <div className="bg-orange-100 p-2 rounded-lg">
                <span className="text-orange-600">⚠️</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Need immediate attention
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.dispute_stats?.[0]?.resolved_disputes || 0}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <span className="text-green-600">✅</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Successfully resolved
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Recent</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats.dispute_stats?.[0]?.recent_disputes || 0}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <span className="text-blue-600">🕐</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Last 7 days
            </p>
          </div>
        </div>
      )}

      {/* Priority Alert */}
      {disputes.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <span className="text-red-600">🚨</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-900">Priority Attention Required</h3>
                <p className="text-red-700">
                  You have {disputes.length} active dispute{disputes.length !== 1 ? 's' : ''} waiting for resolution. 
                  Please resolve within 48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Disputes List */}
      <div className="space-y-4">
        {disputes.map((dispute) => {
          const severity = getDisputeSeverity(dispute);
          return (
            <div key={dispute.order_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              {/* Dispute Header */}
              <div className={`p-4 border-b ${
                severity === 'high' ? 'bg-red-50 border-red-200' :
                severity === 'medium' ? 'bg-orange-50 border-orange-200' :
                'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-red-500">⚖️</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">Order #{dispute.order_id}</h3>
                      <p className="text-sm text-gray-600">{dispute.listing_title}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getSeverityBadge(severity)}
                    <span className="text-sm text-gray-500">
                      {getTimeAgo(dispute.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dispute Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Order Details */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Order Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Amount</span>
                        <span className="text-sm font-bold text-green-600">
                          {formatCurrency(dispute.total_amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {getStatusBadge(dispute.status)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Escrow</span>
                        <span className="text-sm text-gray-900">
                          {dispute.escrow_status || 'No escrow'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Buyer Information */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">👤 Buyer</h4>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">
                        {dispute.buyer_first_name} {dispute.buyer_last_name}
                      </p>
                      <p className="text-sm text-gray-600">{dispute.buyer_email}</p>
                      <button
                        onClick={() => navigate(`/admin/users/${dispute.buyer_id}`)}
                        className="text-blue-600 hover:text-blue-700 text-xs transition-colors"
                      >
                        View Profile →
                      </button>
                    </div>
                  </div>

                  {/* Seller Information */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">🏪 Seller</h4>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">{dispute.seller_store_name}</p>
                      <p className="text-sm text-gray-600">{dispute.seller_email}</p>
                      <button
                        onClick={() => navigate(`/admin/sellers/${dispute.seller_id}`)}
                        className="text-blue-600 hover:text-blue-700 text-xs transition-colors"
                      >
                        View Store →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate(`/admin/orders/${dispute.order_id}`)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    View Full Details
                  </button>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => openResolutionModal(dispute)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                    >
                      Resolve Dispute
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {disputes.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-green-500 text-6xl mb-4">🎉</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Disputes!</h3>
          <p className="text-gray-500 mb-4">All disputes have been resolved. Great work!</p>
          <button
            onClick={() => navigate('/admin/orders')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View All Orders
          </button>
        </div>
      )}

      {/* Resolution Modal */}
      {showResolutionModal && selectedDispute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Resolve Dispute - Order #{selectedDispute.order_id}
            </h3>

            {/* Dispute Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Dispute Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Listing:</span>
                  <p className="font-medium">{selectedDispute.listing_title}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium text-green-600">
                    {formatCurrency(selectedDispute.total_amount)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Buyer:</span>
                  <p className="font-medium">
                    {selectedDispute.buyer_first_name} {selectedDispute.buyer_last_name}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Seller:</span>
                  <p className="font-medium">{selectedDispute.seller_store_name}</p>
                </div>
              </div>
            </div>

            {/* Resolution Options */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Resolution Type *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Full Refund Option */}
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      resolutionData.resolution === 'refund_buyer' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setResolutionData(prev => ({
                      ...prev,
                      resolution: 'refund_buyer',
                      refund_amount: selectedDispute.total_amount
                    }))}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        resolutionData.resolution === 'refund_buyer' 
                          ? 'border-red-500 bg-red-500' 
                          : 'border-gray-300'
                      }`}></div>
                      <span className="font-medium text-gray-900">Full Refund</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Refund full amount ({formatCurrency(selectedDispute.total_amount)}) to buyer
                    </p>
                    <p className="text-xs text-red-600 mt-2">
                      Order will be cancelled
                    </p>
                  </div>

                  {/* Release to Seller Option */}
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      resolutionData.resolution === 'release_seller' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setResolutionData(prev => ({
                      ...prev,
                      resolution: 'release_seller',
                      refund_amount: 0
                    }))}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        resolutionData.resolution === 'release_seller' 
                          ? 'border-green-500 bg-green-500' 
                          : 'border-gray-300'
                      }`}></div>
                      <span className="font-medium text-gray-900">Release Funds</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Release full amount to seller
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      Order will be completed
                    </p>
                  </div>

                  {/* Partial Refund Option */}
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      resolutionData.resolution === 'partial_refund' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setResolutionData(prev => ({
                      ...prev,
                      resolution: 'partial_refund',
                      refund_amount: selectedDispute.total_amount * 0.5 // Default 50%
                    }))}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        resolutionData.resolution === 'partial_refund' 
                          ? 'border-orange-500 bg-orange-500' 
                          : 'border-gray-300'
                      }`}></div>
                      <span className="font-medium text-gray-900">Partial Refund</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Split amount between buyer and seller
                    </p>
                    <p className="text-xs text-orange-600 mt-2">
                      Custom amount required
                    </p>
                  </div>
                </div>
              </div>

              {/* Partial Refund Amount */}
              {resolutionData.resolution === 'partial_refund' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Amount *
                  </label>
                  <div className="flex space-x-4">
                    <input
                      type="number"
                      value={resolutionData.refund_amount}
                      onChange={(e) => setResolutionData(prev => ({
                        ...prev,
                        refund_amount: parseFloat(e.target.value) || 0
                      }))}
                      placeholder="Enter refund amount"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      max={selectedDispute.total_amount}
                      step="0.01"
                    />
                    <div className="text-sm text-gray-600 flex items-center">
                      of {formatCurrency(selectedDispute.total_amount)}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Seller will receive: {formatCurrency(selectedDispute.total_amount - resolutionData.refund_amount)}
                  </p>
                </div>
              )}

              {/* Resolution Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resolution Notes *
                </label>
                <textarea
                  value={resolutionData.resolution_notes}
                  onChange={(e) => setResolutionData(prev => ({
                    ...prev,
                    resolution_notes: e.target.value
                  }))}
                  placeholder="Explain the resolution decision and any relevant details..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="4"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These notes will be visible in the order history and may be shared with parties involved.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowResolutionModal(false);
                  setSelectedDispute(null);
                  setResolutionData({
                    resolution: '',
                    refund_amount: 0,
                    resolution_notes: ''
                  });
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveDispute}
                disabled={!resolutionData.resolution || !resolutionData.resolution_notes.trim() || actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Resolving...' : 'Resolve Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDisputes;